import React, { useEffect, useState } from "react";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

const steps = [
  { num: 1, title: "Share your code", desc: "Share your unique referral code with friends and family" },
  { num: 2, title: "Friend signs up", desc: "Your friend creates an account using your referral code" },
  { num: 3, title: "Both earn rewards", desc: "You get ₹50 and your friend gets ₹25 wallet credits" },
];

function Refer() {
  const [fetching, setFetching] = useState(true);
  const [referCode, setReferCode] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [totalEarned, setTotalEarned] = useState(0);

  useEffect(() => {
    const fetchRefer = async () => {
      try {
        const result = await apiCall(
          "refer-fetch",
          { refer_code: "ABC123" }, // Pass the refer code from user's profile
          true
        );

        if (result.status) {
          setReferCode(result.refer_code || "ABC123");
          setReferrals(result.refer_user || []);
          
          // Calculate total earned
          const total = (result.refer_user || []).reduce((sum, ref) => sum + (parseInt(ref.balance) || 0), 0);
          setTotalEarned(total);
        } else {
          toast.error(result.message || "An error occurred. Please try again.");
        }
      } catch (error) {
        toast.error("Network error. Please try again.");
      } finally {
        setFetching(false);
      }
    };
    fetchRefer();
  }, []);

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(referCode).then(() => {
        toast.success("Referral code copied!");
      }).catch(() => {
        toast.error("Failed to copy code");
      });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Join ${APP_THEME.appName}`,
        text: `Use my referral code ${referCode} to sign up on ${APP_THEME.appName} and get ₹25 bonus!`,
      }).catch(() => {});
    } else {
      handleCopy();
    }
  };

  // Loading state
  if (fetching) {
    return (
      <>
        <Header title={"Refer Code"} showBack={true} />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm z-10"></div>
          <div className="relative z-20 flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" 
                 style={{ borderColor: APP_THEME.primaryColor, borderTopColor: 'transparent' }}></div>
            <p className="mt-4 text-sm font-medium" style={{ color: APP_THEME.textPrimary }}>
              Loading referrals...
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header title={"Refer Code"} showBack={true} />
      <div className="p-4">
        {/* Hero Card */}
        <div
          className="rounded-card p-6 mb-6 text-white animate-fadeIn"
          style={{
            background: `linear-gradient(135deg, ${APP_THEME.primaryColor}, ${APP_THEME.primaryDark})`,
            borderRadius: APP_THEME.borderRadiusCard,
            boxShadow: `0 8px 24px ${APP_THEME.primaryColor}30`,
          }}
        >
          <h2 className="text-md font-bold mb-2" style={{ fontFamily: APP_THEME.fontFamily }}>
            Earn ₹10 for every friend you refer!
          </h2>

          {/* Referral Code Box */}
          <div
            className="rounded-xl p-3 text-center mb-4"
            style={{
              background: APP_THEME.primaryLight,
            }}
          >
            <div
              className="text-md font-bold tracking-wider"
              style={{
                fontFamily: "'Courier New', monospace",
                color: APP_THEME.primaryColor,
                letterSpacing: "4px",
              }}
            >
              {referCode}
            </div>
          </div>

          <div className="flex gap-2.5">
            <button
              onClick={handleCopy}
              className="flex-1 h-10 rounded-lg text-[13px] font-semibold cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-200 active:scale-95"
              style={{
                background: "rgba(255,255,255,0.2)",
                color: APP_THEME.textOnPrimary,
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: APP_THEME.borderRadiusBtn,
                fontFamily: APP_THEME.fontFamily,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy Code
            </button>
            <button
              onClick={handleShare}
              className="flex-1 h-10 border-none rounded-lg text-[13px] font-semibold cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-200 active:scale-95"
              style={{
                background: APP_THEME.bgColor,
                color: APP_THEME.primaryColor,
                borderRadius: APP_THEME.borderRadiusBtn,
                fontFamily: APP_THEME.fontFamily,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              Share
            </button>
          </div>
        </div>

        {/* How It Works */}
        <h3
          className="text-base font-bold mb-4"
          style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
        >
          How It Works
        </h3>
        <div className="mb-7 pl-2">
          {steps.map((step, index) => (
            <div key={step.num} className="flex gap-3.5 relative pb-6 last:pb-0">
              {index < steps.length - 1 && (
                <div
                  className="absolute left-[15px] top-9 bottom-0 w-0.5"
                  style={{ background: APP_THEME.primaryLight }}
                />
              )}
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 z-[1]"
                style={{
                  background: APP_THEME.primaryColor,
                  color: APP_THEME.textOnPrimary,
                  fontFamily: APP_THEME.fontFamily,
                }}
              >
                {step.num}
              </div>
              <div>
                <div
                  className="text-sm font-semibold mb-0.5"
                  style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                >
                  {step.title}
                </div>
                <div
                  className="text-xs leading-relaxed"
                  style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                >
                  {step.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total Earned */}
        <div
          className="rounded-card p-4 mb-5 flex items-center justify-between border"
          style={{
            background: APP_THEME.surfaceColor,
            borderColor: APP_THEME.borderColor,
            borderRadius: APP_THEME.borderRadiusCard,
          }}
        >
          <span
            className="text-sm font-medium"
            style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
          >
            Total Earned
          </span>
          <span
            className="text-2xl font-bold"
            style={{ color: APP_THEME.successColor, fontFamily: APP_THEME.fontFamily }}
          >
            ₹{totalEarned}
          </span>
        </div>

        {/* My Referrals */}
        <h3
          className="text-base font-bold mb-3"
          style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
        >
          My Referrals ({referrals.length})
        </h3>
        
        {referrals.length === 0 ? (
          <div
            className="text-center py-8 px-1 rounded-card border"
            style={{
              background: APP_THEME.surfaceColor,
              borderColor: APP_THEME.borderColor,
              borderRadius: APP_THEME.borderRadiusCard,
            }}
          >
            <p className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
              No referrals yet. Share your code to earn rewards!
            </p>
          </div>
        ) : (
          referrals.map((ref, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 py-3"
              style={{
                borderBottom: idx < referrals.length - 1 ? `1px solid ${APP_THEME.borderColor}` : "none",
              }}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold"
                style={{
                  background: APP_THEME.primaryLight,
                  color: APP_THEME.primaryColor,
                  fontFamily: APP_THEME.fontFamily,
                }}
              >
                {ref.name ? ref.name.charAt(0).toUpperCase() : "U"}
              </div>
              <div className="flex-1">
                <div
                  className="text-sm font-semibold"
                  style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                >
                  {ref.name || "User"}
                </div>
                <div
                  className="text-xs"
                  style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                >
                  {ref.mobile || "No mobile"}
                </div>
              </div>
              <div className="text-right">
                <span
                  className="text-[10px] font-semibold py-0.5 px-2 rounded"
                  style={{
                    color: (parseInt(ref.balance) || 0) > 0 ? APP_THEME.successColor : APP_THEME.warningColor,
                    background: (parseInt(ref.balance) || 0) > 0 ? `${APP_THEME.successColor}18` : `${APP_THEME.warningColor}18`,
                    fontFamily: APP_THEME.fontFamily,
                  }}
                >
                  {(parseInt(ref.balance) || 0) > 0 ? "Active" : "Pending"}
                </span>
                {(parseInt(ref.balance) || 0) > 0 && (
                  <div
                    className="text-[13px] font-bold mt-1"
                    style={{ color: APP_THEME.successColor, fontFamily: APP_THEME.fontFamily }}
                  >
                    +₹{ref.balance}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <Toaster position="bottom-center" />

      <style>
        {`
          .active\\:scale-95:active {
            transform: scale(0.95);
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.4s ease forwards;
          }
        `}
      </style>
    </>
  );
}

export default Refer;